import {
  DocumentIntegrationsState,
  DocumentsStore,
  IntegrationState,
  PiiTypeId,
} from './models';

export const namespace = 'vtn-redact-state-documents';

export const MIN_DIMENSION = 5;
export const UNDO_STACK_LIMIT = 100;
export const KEYWORD_SEARCH_DEBOUNCE_MS = 250;
export const AUTO_SAVE_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes per AD-12

// --- Persistence (VE-20835) -------------------------------------------------
/** Veritone asset type used for the saved redaction data JSON. */
export const DOCUMENT_REDACTION_ASSET_TYPE = 'document-redaction-data';
/**
 * Veritone asset type holding ONE image watermark's raw raster (VE-24693, §J).
 *
 * Written once per (document, watermark) and never rewritten: the asset carries
 * only the immutable artwork, while every mutable attribute — grayscale, tint,
 * scale, opacity, rotation, placement — stays in the redaction-data body. So
 * adjusting a mark costs a JSON save, not an image upload, and the stored id
 * always resolves to exactly the artwork that was applied (AC#3).
 *
 * Deliberately its own type rather than a key inside redaction-data: ~40k
 * characters of base64 in the middle of that body made it unreadable by hand,
 * which matters for a file that gets inspected whenever a redaction is
 * disputed. Safe to add because every casemedia asset query filters by an
 * explicit `assetType` allowlist, so a new type cannot make a file read as
 * redacted or surface in Download Review.
 */
export const DOCUMENT_WATERMARK_IMAGE_ASSET_TYPE = 'document-watermark-image';
/**
 * Format version stamped into each persisted asset body AND mirrored into
 * the asset's GraphQL `details` metadata so callers can filter by format
 * without downloading the body. Distinct from the document's own version
 * (see `RedactedVersion[].versionId`) — this is the schema of the saved
 * JSON, not the version of the redacted output.
 */
// 0.0.3 (data-model cleanup): geometry persists as `points` only (no stored
// bounding box — derived on load); custom polygons live under the `manual`
// origin via `isCustomPolygon`.
// 1.0.0: adds per-row `wordCount` on text-select entries so word-count metrics
// can be totaled without re-deriving from `phrase`.
// 1.1.0: page-level flags renamed to match the Remove Page / Delete Page
// vocabulary — `pageFullyRedacted` -> `pageContentRemoved`, `pageRemoved` ->
// `pageHidden`. The 1.0.0 names are still written alongside the new ones so a
// rollback to a pre-1.1.0 build reads the flags correctly; drop the dual-write
// (and the legacy read fallback in helpers.ts) once no such build is deployed.
// 1.2.0: adds the top-level `watermarks` array (VE-24693).
// 1.3.0: an image watermark persists `imageAssetId` — a reference to its own
// `document-watermark-image` asset — instead of an inline `imageBaseDataUrl`.
// The raster moved out of the body; nothing else about a mark changed. A 1.2.0
// asset still loads: `watermarksFromAssetBody` reads whichever of the two an
// asset carries, so a document saved before this change keeps working from its
// inline base until its next save.
// 1.5.0 (VE-24948, custom lexicon): adds root `lexiconDetections`, root
// `appliedLexicons`, and a per-page `LEXICON` bucket. ROLLBACK IS LOSSY, and
// unlike the versions above it cannot be made otherwise by dual-writing: an
// older build reads this asset cleanly (its `PERSISTED_ORIGINS` whitelist
// simply skips the new keys), drops every lexicon row, and its next autosave
// writes the truncated body back — permanently discarding applied lexicon
// geometry and triage state on a document that may already have been exported
// with those redactions burned in, with nothing in the asset to detect it. Do
// not roll a deployment back below this format once lexicons are in use; see
// docs/schemas/LexiconSchema.md.
//
// Renumbered TWICE on merge — 1.2.0 on the lexicon branch, then 1.4.0, now
// this — because watermarks and then Bates stamping (VE-24447) landed first.
// All three are independent, so the order is arbitrary; what matters is that
// one number means one body shape.
export const DOCUMENT_REDACTION_ASSET_FORMAT_VERSION = '1.5.0' as const;

/**
 * What a body with NO lexicon data is stamped with.
 *
 * `buildRedactionAssetBody` picks between the two, so a document that never had
 * a lexicon applied still serializes exactly as a build without this feature
 * would write it (BR-52/53) — the version field is part of those bytes. It also
 * keeps `details.assetFormatVersion` useful as the rollback blast-radius
 * detector it exists to be: only documents that actually carry lexicon data
 * report 1.5.0, so an operator can find precisely the ones a rollback would
 * truncate.
 *
 * Tracks the highest NON-lexicon version, and MOVED TO 1.4.0 ON THIS MERGE.
 * Bates stamping is exactly the case this doc comment warned about: it adds a
 * non-lexicon key, so leaving this at 1.3.0 would stamp a Bates-stamped
 * document with a version predating its own contents. It went 1.1.0 -> 1.3.0
 * for watermarks and 1.3.0 -> 1.4.0 here for the same reason.
 */
export const DOCUMENT_REDACTION_ASSET_FORMAT_VERSION_PRE_LEXICON =
  '1.4.0' as const;
/**
 * Minimum asset format the loader will read. Anything BELOW this is ignored on
 * open (no migration). 0.0.3 is still accepted, but its glyph-anchored boxes
 * carry a baked-in +2 pad that the current model doesn't — the open path runs
 * them through a load adapter (see `stripLegacyGlyphPad`) to normalize to the
 * neutral box. Bump this when a future format genuinely can't be adapted.
 */
export const MIN_SUPPORTED_ASSET_FORMAT_VERSION = '0.0.3' as const;

/**
 * Whether a stored asset's `assetFormatVersion` is new enough to load —
 * `version >= MIN_SUPPORTED_ASSET_FORMAT_VERSION`. Parses dotted numeric
 * versions ("a.b.c"); anything missing or malformed is treated as unsupported.
 */
export function isAssetFormatSupported(version: string | undefined): boolean {
  if (!version || !/^\d+\.\d+\.\d+$/.test(version)) {
    return false;
  }
  const parse = (v: string) => v.split('.').map((n) => Number(n));
  const a = parse(version);
  const b = parse(MIN_SUPPORTED_ASSET_FORMAT_VERSION);
  for (let i = 0; i < 3; i++) {
    const diff = (a[i] ?? 0) - (b[i] ?? 0);
    if (diff !== 0) {
      return diff > 0;
    }
  }
  return true; // exactly equal
}
/** Decimal precision (digits after the point) used when serializing coordinates. */
export const DOCUMENT_REDACTION_COORD_PRECISION = 3;
/** Asset rotation: only the most-recent N saves are retained on the TDO. */
export const DOCUMENT_REDACTION_ASSET_HISTORY_LIMIT = 5;

export const DIRECT_PII_TYPES: PiiTypeId[] = [
  'full_name',
  'ssn',
  'dob',
  'phone',
  'email',
  'address',
  'license_plate',
  'credit_card',
  'medical_record_number',
  'ip_address',
];

export const INDIRECT_PII_TYPES: PiiTypeId[] = [
  'employer',
  'school',
  'race',
  'gender',
  'age',
  'religion',
  'salary',
  'vehicle',
  'ip_prefix',
  'other',
];

export const ALL_PII_TYPES: PiiTypeId[] = [
  ...DIRECT_PII_TYPES,
  ...INDIRECT_PII_TYPES,
];

/** A fresh, idle integration slice. */
const emptyIntegration = (): IntegrationState => ({
  isSendingFile: false,
  isFormShown: false,
  isStatusDialogShown: false,
});

/** Default "Send to" integration state — all six slices idle (VE-23442). */
export const emptyIntegrationsState = (): DocumentIntegrationsState => ({
  govQa: emptyIntegration(),
  foiaXpress: emptyIntegration(),
  casepoint: emptyIntegration(),
  exterro: emptyIntegration(),
  nuix: emptyIntegration(),
  externalIntegration: emptyIntegration(),
});

export const defaultState: DocumentsStore = {
  session: null,
  suspendedSessions: {},
  pendingDocument: null,
  ocrReloadInProgress: false,
  uploadLexiconSelection: [],
  uploadLexiconAutoRedact: [],
  uploadLexiconBatch: null,
  loadError: null,
  orgConfigUnavailable: false,
  documentComments: {},
  documentCommentDrafts: {},
  isFetchingDocumentComments: false,
  selectedDocumentComments: [],
  editingDocumentCommentId: null,
  focusedDocumentCommentId: null,
  batesPreview: null,
  watermarkPreview: null,
  integrations: emptyIntegrationsState(),
  documentsPanelOpen: false,
  documentsPanelPinned: false,
  toolPanelExpanded: false,
  documentOrder: null,
  documentsPanelNotIncludedCollapsed: false,
  documentsPanelMergedCollapsed: false,
  documentsPanelMembershipSnapshot: null,
  documentsPanelEditingMembership: false,
  saveAllInProgress: false,
  redactAll: null,
  burnProgress: null,
  redactAllMinimised: false,
  documentsPanelExpanded: false,
  burnInsStartedHere: {},
  pagesPanelCollapsed: false,
  declinedUnworkedKey: null,
};

// Sentinel used when the session has no page text yet (before PDF renders).
export const EMPTY_PAGE_TEXTS: Record<number, string> = {};

export interface RedactDocumentsState {
  [namespace]: DocumentsStore;
}
